import { isDefined } from '@sgme/fp';
import type { LimitOrderAmendRequest } from 'apiConnector/secondary-market/limit-order/models';
import { createSelector } from 'reselect';
import type { LimitOrderForm } from 'state/secondaryMarket/limit-order/form/input/inputForm.model';
import { limitOrderInputFormSelectors } from 'state/secondaryMarket/limit-order/form/input/inputForm.selectors';
import { limitOrderOriginalFormSelectors } from 'state/secondaryMarket/limit-order/form/original/original.selector';
import { limitOrderReferentialSelectors } from 'state/secondaryMarket/limit-order/referential/referential.selector';
import type { SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { StreamingPriceForDisplay } from 'state/secondaryMarket/realtime/realtime.selector';
import { getStreamingPriceForDisplayByTradeway } from 'state/secondaryMarket/realtime/realtime.selector';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import { MISSING_CLIENT_LIMIT } from 'state/secondaryMarket/shared/form/validationResults';
import { secondaryMarketSelectorsFactory } from 'state/secondaryMarket/shared/selector';
import { computeCashAmount } from 'state/secondaryMarket/shared/utils';
import { bcsProductKeyToString } from 'state/secondaryMarket/utils';
import { fromSecondaryQuotesWorkspace } from 'state/selectors';
import { getBcsProductKeyByWorkspace } from 'state/selectors/common/tabs';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import * as fromSecondaryPostEventsWorkspace from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.selector';
import { getProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';

import type { LimitOrderStatus } from './limitOrder.model';
import { limitOrderTradeSelectors } from './order/order.selector';

const emptyArray: readonly [] = [];

const missingLimitError: readonly SecondaryMarketFormError<LimitOrderForm>[] = [MISSING_CLIENT_LIMIT];

const getValidationErrors = (
  appState: AppState,
  tabId: string,
): readonly SecondaryMarketFormError<LimitOrderForm>[] => {
  const limit = limitOrderInputFormSelectors.getLimit(appState, tabId);
  if (limit === undefined) {
    return missingLimitError;
  } else {
    return emptyArray;
  }
};
const getAmendValidationErrors = (
  appState: AppState,
  tabId: string,
): readonly SecondaryMarketFormError<LimitOrderForm>[] => {
  const limit = limitOrderSelectors.getLimit(appState, tabId);
  if (limit === undefined) {
    return missingLimitError;
  } else {
    return emptyArray;
  }
};
const selectors = secondaryMarketSelectorsFactory<LimitOrderForm, {}>('limitOrder', {
  getValidationErrors,
  getAmendValidationErrors,
});

const getAmendRequest = (state: AppState, tabId: string): LimitOrderAmendRequest => {
  const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);
  const client = limitOrderInputFormSelectors.getFormClient(state, tabId);
  const quantity = limitOrderInputFormSelectors.getFormQuantity(state, tabId);
  const modified = limitOrderInputFormSelectors.getFormChanges(state, tabId);
  const settlementType = selectors.getFormSettlement(state, tabId);

  return {
    clientType: limitOrderInputFormSelectors.getFormClientType(state, tabId),
    clientBdrId: client?.id,
    clientEliotCode: client?.eliotCode,
    clientLimit: limitOrderInputFormSelectors.getLimit(state, tabId),
    custodianBdrId: limitOrderInputFormSelectors.getFormCustodianBdrId(state, tabId),
    salesMarginAdjustment: limitOrderInputFormSelectors.getSalesMarginAdjustment(state, tabId),
    salesId: selectors.getFormSalesId(state, tabId),
    orderId: orderId,
    settlementDate: limitOrderInputFormSelectors.getFormSettlementDate(state, tabId),
    settlementType: modified.includes('settlementType') ? settlementType : undefined,
    comment: limitOrderInputFormSelectors.getFormComment(state, tabId),
    priceType: limitOrderOriginalFormSelectors.getPriceType(state, tabId),
    quantity: quantity,
    productKey: fromSecondaryQuotesWorkspace.getProductKey(state, tabId),
  } satisfies LimitOrderAmendRequest;
};

export const getOrderStatus = createSelector(
  [
    (appState: AppState, tabId: string) =>
      selectors
        .makeGetValidationErrors()(appState, tabId)
        .filter(e => e.isMandatory).length,
    limitOrderTradeSelectors.getTradeStatus,
  ],
  (errorsCnt, tradeStatus) => {
    if (tradeStatus !== undefined) {
      return tradeStatus as LimitOrderStatus;
    }
    return errorsCnt > 0 ? ('NOT_READY' as LimitOrderStatus) : ('READY' as LimitOrderStatus);
  },
);
const getQuotationType = (appState: AppState, tabId: string): SecondaryQuotationType | undefined =>
  limitOrderReferentialSelectors.getQuotationType(appState, tabId);

const getEffectiveCashAmount = (_appState: AppState, _tabId: string) => undefined;

export const getCashAmountPerQuantityUnit = (state: AppState, tabId: string) => {
  const denomination = limitOrderReferentialSelectors.getDenomination(state, tabId);
  const priceType = limitOrderReferentialSelectors.getPriceType(state, tabId)!;
  const limit = limitOrderSelectors.getLimit(state, tabId);
  return computeCashAmount(limit, denomination, priceType);
};

const getKeepAliveProductIds = (state: AppState) => {
  const productIds = Object.entries(state.secondaryMarket.limitOrder.form.input)
    .map(([tabId]) => getProductKey(state, tabId))
    .filter(isDefined);
  return new Set(productIds);
};

const getStreamingPriceForDisplay = (
  appState: AppState,
  tabId: string,
  workspace: SecondaryWorkspaceType,
): StreamingPriceForDisplay | undefined => {
  const tradeWay = limitOrderSelectors.getFormTradeWay(appState, tabId);
  if (!tradeWay) {
    return;
  }

  return getStreamingPriceForDisplayByTradeway(
    appState,
    bcsProductKeyToString(getBcsProductKeyByWorkspace(appState, workspace, tabId)),
    tradeWay,
  );
};
const getLimit = (state: AppState, tabId: string) => {
  return limitOrderInputFormSelectors.getLimit(state, tabId) ?? limitOrderOriginalFormSelectors.getLimit(state, tabId);
};
const getSalesMarginAdjustment = (state: AppState, tabId: string) => {
  return (
    limitOrderInputFormSelectors.getSalesMarginAdjustment(state, tabId) ??
    limitOrderOriginalFormSelectors.getSalesMarginAdjustment(state, tabId)
  );
};
export const limitOrderSelectors = {
  ...selectors,
  getAmendRequest,
  getOrderStatus,
  getQuotationType,
  getEffectiveCashAmount,
  getCashAmountPerQuantityUnit,
  getKeepAliveProductIds,
  getStreamingPriceForDisplay,
  getLimit,
  getSalesMarginAdjustment,
};

export type LimitOrderSelectors = typeof limitOrderSelectors;
